import { useState } from 'react'
import { Check } from 'lucide-react'

type GalleryImage = {
  src: string
  alt: string
}

const galleryImages: GalleryImage[] = [
  {
    src: '/assets/images/image 247.png',
    alt: 'Panoramic view of Salzburg and Hohensalzburg Fortress',
  },
  {
    src: '/assets/images/image 242.png',
    alt: 'Salzburg skyline during a warm sunset',
  },
]

const storyPoints = [
  'Professionally Trained',
  '24/7 Full-Service Transportation',
  'Global Network of Transportation Partner',
]

export default function AboutContent() {
  const [imagesReversed, setImagesReversed] = useState(false)

  const visibleImages = imagesReversed
    ? [...galleryImages].reverse()
    : galleryImages

  const showPreviousImage = () => {
    setImagesReversed((currentValue) => !currentValue)
  }

  const showNextImage = () => {
    setImagesReversed((currentValue) => !currentValue)
  }

  return (
    <section className="bg-white px-5 pb-20 pt-10 sm:px-8 sm:pt-14 lg:px-11 lg:pb-[120px] lg:pt-[54px]">
      <div className="mx-auto max-w-page">
        {/* Main about row */}
        <div className="grid items-center gap-12 lg:grid-cols-[0.88fr_1.12fr] lg:gap-[90px] xl:gap-[120px]">
          <div className="max-w-[520px] lg:pl-[70px] xl:pl-[100px]">
            <span className="inline-flex rounded-full border border-[#D7D7D7] bg-[#F4F4F4] px-[18px] py-[6px] font-poppins text-[12px] font-medium leading-none text-brand-orange">
              About Us
            </span>

            <p className="mt-[18px] max-w-[420px] font-host text-[15px] leading-[1.55] tracking-[-0.02em] text-[#676767] sm:text-[16px]">
              Travel with confidence, comfort, and professional service every
              mile of the way.
            </p>

            <h2 className="mt-[12px] max-w-[520px] font-host text-[35px] font-semibold leading-[1.2] tracking-[-0.045em] text-black sm:text-[42px] lg:text-[46px]">
              More Than a Ride,
              <span className="block">A Better Way to Travel.</span>
            </h2>
          </div>

          <div className="relative mx-auto w-full max-w-[690px]">
            <div className="mb-[18px] flex items-center justify-center gap-[18px] lg:justify-start lg:pl-[145px]">
              <button
                type="button"
                onClick={showPreviousImage}
                aria-label="Show previous image"
                className="flex h-[42px] w-[42px] items-center justify-center transition-transform duration-300"
              >
                <img
                  src="/assets/images/image 246.png"
                  alt=""
                  className="h-[30px] w-[30px] object-contain"
                />
              </button>

              <button
                type="button"
                onClick={showNextImage}
                aria-label="Show next image"
                className="flex h-[42px] w-[42px] items-center justify-center transition-transform duration-300"
              >
                <img
                  src="/assets/images/image 246-1.png"
                  alt=""
                  className="h-[30px] w-[30px] object-contain"
                />
              </button>
            </div>

            <div className="grid grid-cols-2 items-start gap-4 sm:gap-6">
              <div className="pt-[44px] sm:pt-[58px]">
                <img
                  key={visibleImages[0].src}
                  src={visibleImages[0].src}
                  alt={visibleImages[0].alt}
                  className="h-[280px] w-full rounded-[22px] object-cover transition-opacity duration-300 sm:h-[350px] lg:h-[380px]"
                />
              </div>

              <img
                key={visibleImages[1].src}
                src={visibleImages[1].src}
                alt={visibleImages[1].alt}
                className="h-[340px] w-full rounded-[22px] object-cover transition-opacity duration-300 sm:h-[430px] lg:h-[470px]"
              />
            </div>
          </div>
        </div>

        {/* Story row */}
        <div className="mt-[76px] grid items-center gap-14 sm:mt-[92px] lg:mt-[110px] lg:grid-cols-[1fr_0.9fr] lg:gap-[100px]">
          <div className="max-w-[590px] lg:pl-[180px] xl:pl-[210px]">
            <span className="inline-flex rounded-full border border-[#D7D7D7] bg-[#F4F4F4] px-[18px] py-[6px] font-poppins text-[12px] font-medium leading-none text-brand-orange">
              Our Story
            </span>

            <h3 className="mt-[18px] font-host text-[25px] font-semibold leading-tight tracking-[-0.035em] text-black sm:text-[28px]">
              Built Around Travelers
            </h3>

            <p className="mt-[13px] max-w-[520px] font-host text-[15px] leading-[1.6] tracking-[-0.015em] text-[#343434] sm:text-[16px]">
              Salzburg Cab began with a simple goal: to provide reliable,
              comfortable, and stress-free transportation for travelers.
            </p>

            <div className="mt-[20px] flex flex-col gap-[14px]">
              {storyPoints.map((point) => (
                <div key={point} className="flex items-center gap-[11px]">
                  <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-black">
                    <Check
                      className="h-[13px] w-[13px] text-brand-green"
                      strokeWidth={3}
                    />
                  </span>

                  <p className="font-host text-[15px] font-medium tracking-[-0.015em] text-[#242424] sm:text-[16px]">
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center lg:items-end lg:pr-[125px] xl:pr-[170px] lg:pt-[70px]">
            <img
              src="/assets/images/image 259.png"
              alt="Historic buildings in Salzburg"
              className="h-[190px] w-[230px] rounded-[20px] object-cover sm:h-[205px] sm:w-[255px]"
            />

            <p className="mt-[26px] max-w-[390px] text-center font-host text-[15px] leading-[1.55] tracking-[-0.015em] text-[#292929] sm:text-[16px]">
              We help travelers enjoy safe, comfortable, and stress-free
              journeys across Austria and Europe.
            </p>
          </div>
        </div>
        

<div className="mx-auto mt-20 w-full max-w-[1090px] px-1 sm:mt-24 sm:px-2 lg:mt-[105px]">
  <div className="grid items-stretch gap-6 lg:grid-cols-[340px_1fr] lg:gap-[32px]">
   
<div className="overflow-hidden rounded-t-[18px] rounded-b-[22px]">
  <img
    src="/assets/images/image 245.png"
    alt="Scenic transportation route through the mountains"
    className="h-[450px] w-full object-cover sm:h-[450px] lg:h-[450px]"
  />
</div>

   
    <div className="flex flex-col gap-3">
    
      <div className="flex min-h-[96px] items-center justify-between rounded-[16px] bg-[#F0F0F0] px-5 py-4 sm:px-6">
       
        <div className="flex shrink-0 items-center">
          <img
            src="/assets/images/Ellipse 73.png"
            alt=""
            className="relative z-30 h-[52px] w-[52px] rounded-full object-cover"
          />

          <img
            src="/assets/images/Ellipse 74.png"
            alt=""
            className="relative z-20 -ml-[15px] h-[52px] w-[52px] rounded-full object-cover"
          />

          <img
            src="/assets/images/Ellipse 75.png"
            alt=""
            className="relative z-10 -ml-[15px] h-[52px] w-[52px] rounded-full object-cover"
          />
        </div>

        <h3 className="ml-5 font-host text-[20px] font-semibold leading-[1.35] tracking-[-0.03em] text-black sm:text-[22px]">
          15 Years Of
          <span className="block">Experience</span>
        </h3>
      </div>

      
      <div className="grid gap-3 md:grid-cols-[1.03fr_0.97fr]">
        
        <div className="flex flex-col gap-3">
       
          <div className="flex min-h-[198px] flex-col justify-between rounded-[18px] bg-[#F0F0F0] px-4 py-5 sm:px-5">
            <p className="max-w-[220px] font-poppins text-[13px] font-normal leading-[1.55] text-[#111111]">
              Safely connecting travelers to destinations near and far.
            </p>

            <h3 className="mt-8 font-host text-[18px] font-semibold leading-[1.3] tracking-[-0.03em] text-black sm:text-[19px]">
              500,000+
              <span className="block">Journeys Completed</span>
            </h3>
          </div>

          
          <div className="relative min-h-[110px] overflow-hidden rounded-[18px] bg-[#F0F0F0] px-4 py-3">
            <p className="relative z-10 max-w-[215px] font-poppins text-[11px] font-normal leading-[1.45] text-[#111111] sm:text-[12px]">
              A diverse fleet to accommodate individuals, families and
              groups of all sizes.
            </p>

            <h3 className="relative z-10 mt-6 font-host text-[18px] font-semibold tracking-[-0.03em] text-black sm:text-[19px]">
              120+ Vehicles
            </h3>

            <img
              src="/assets/images/image 131.png"
              alt="Salzburg Cab vehicle"
              className="absolute bottom-[-1px] right-[-3px] w-[130px] object-contain sm:w-[145px]"
            />
          </div>
        </div>

        
        <div className="flex min-h-[320px] flex-col justify-between rounded-[18px] bg-[#F0F0F0] px-4 py-5 sm:px-5">
          <p className="max-w-[225px] font-poppins text-[13px] font-normal leading-[1.55] text-[#111111]">
            Providing reliable transportation and travel services across
            Austria and Europe.
          </p>

          <h3 className="mt-10 font-host text-[22px] font-semibold leading-[1.3] tracking-[-0.03em] text-black sm:text-[24px]">
            60k+
            <span className="block">Feedback</span>
          </h3>
        </div>
      </div>
    </div>
  </div>
</div>

      </div>
    </section>
  )
}
